import { useState } from 'react';

export default function CTASection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    formData.delete('phone_alt');

    setFormStatus('submitting');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch('https://readdy.ai/api/form/d9sr42llbrt1qcgaj8jg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后再试。');
        } else {
          setFormError(serverMsg || '提交失败，请稍后再试。');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试。');
      setFormStatus('error');
    }
  };

  return (
    <section className="w-full py-16 md:py-20 bg-accent-500">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
            联系我们，一起守护清朗网络空间
          </h2>
          <p className="text-accent-100 text-sm md:text-base max-w-2xl mx-auto mb-10">
            如果您有关于网络安全的问题、建议或合作意向，欢迎通过以下表单联系我们。我们将在2个工作日内回复。
          </p>

          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="max-w-lg mx-auto bg-background-50 rounded-xl p-6 md:p-8"
          >
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="text-left">
                  <label htmlFor="name" className="block text-foreground-700 text-sm font-medium mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                    placeholder="请输入姓名"
                  />
                </div>
                <div className="text-left">
                  <label htmlFor="email" className="block text-foreground-700 text-sm font-medium mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                    placeholder="请输入邮箱"
                  />
                </div>
              </div>

              <div className="text-left">
                <label htmlFor="subject" className="block text-foreground-700 text-sm font-medium mb-1.5">主题</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                  placeholder="请输入主题"
                />
              </div>

              <div className="text-left">
                <label htmlFor="message" className="block text-foreground-700 text-sm font-medium mb-1.5">留言</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors resize-none"
                  placeholder="请输入留言内容（限500字）"
                ></textarea>
              </div>

              <input
                type="text"
                name="phone_alt"
                className="honeypot-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {formStatus === 'success' && (
                <div className="px-4 py-3 rounded-lg bg-accent-50 border border-accent-200 text-accent-700 text-sm">
                  感谢您的留言！我们将在2个工作日内通过邮件回复您。
                </div>
              )}
              {formStatus === 'error' && formError && (
                <div className="px-4 py-3 rounded-lg bg-primary-50 border border-primary-200 text-primary-700 text-sm">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full px-6 py-3 rounded-lg bg-accent-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-accent-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {formStatus === 'submitting' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中...
                  </span>
                ) : (
                  '发送留言'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}